import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import React, { useCallback, useRef } from 'react'
import type { Key } from 'react-aria-components'
import { Button, Menu, MenuItem, MenuTrigger } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { $api } from '#/api/client'
import { Icon } from '#components/Icon'
import { Popover } from '#components/Popover'
import { useAuth } from '#hooks/useAuth'
import { isManagerOnly, ROLE_MANAGER, ROLE_SUPER_ADMIN } from '#interfaces/auth'

import { runCompanySwitch } from './helpers'
import styles from './styles.module.scss'

interface IAccessibleCompany {
  id: string
  name: string
}

export const CompanySwitcher = (): React.JSX.Element | null => {
  const { t } = useTranslation()
  const { user, hasMinimumRole, refreshUser, isImpersonating } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // mutateAsync is stable. The ref is the in-flight guard: it is set before
  // the first await, so a second pick in the same turn cannot send another PATCH.
  const switchInFlightRef = useRef(false)

  // Role hierarchy: SUPER_ADMIN > ADMIN > MANAGER. Managers and admins
  // hold UserCompany access against specific companies — this dropdown
  // is exactly for them. Super admins are explicitly excluded: they can
  // reach any company via /companies, and the BE returns the entire
  // company collection for them which is neither performant nor a
  // useful UX in a dropdown. The 2+ accessible-companies guard below
  // additionally hides the dropdown from single-company managers.
  // While impersonating, the profile company is the impersonated user's,
  // so a pick would be compared against that company and rolled back.
  const canSwitchCompanies = hasMinimumRole(ROLE_MANAGER) && !hasMinimumRole(ROLE_SUPER_ADMIN) && !isImpersonating

  const { data: companies } = $api.useQuery('get', '/auth/me/accessible-companies', {}, { enabled: canSwitchCompanies })

  const { mutateAsync: switchCompany } = $api.useMutation('patch', '/auth/me/selected-company')

  const accessibleCompanies: IAccessibleCompany[] = (companies?.member ?? []).map((c) => ({
    id: c.id ?? '',
    name: c.name ?? '',
  }))

  const handleSelect = useCallback(
    (key: Key): void => {
      const companyId = String(key)
      if (companyId === user?.companyId || switchInFlightRef.current) return

      switchInFlightRef.current = true
      const location = {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      }

      const run = async (): Promise<void> => {
        try {
          // confirm: none — MAG-145 switching company changes the working company, not company data, and is undone by picking it back
          await runCompanySwitch({
            companyId,
            oldCompanyId: user?.companyId,
            location,
            fallback: isManagerOnly(user?.roles) ? 'day-cards' : 'company',
            switchCompany,
            refreshUser,
            navigate: (options) => {
              if ('href' in options) return navigate({ href: options.href })
              if (options.to === '/companies/$companyId/day-cards')
                return navigate({ to: '/companies/$companyId/day-cards', params: options.params })
              return navigate({ to: '/companies/$companyId', params: options.params })
            },
            queryClient,
            t,
          })
        } finally {
          switchInFlightRef.current = false
        }
      }

      void run()
    },
    [navigate, queryClient, refreshUser, switchCompany, t, user?.companyId, user?.roles],
  )

  // Only render once we have multiple companies to choose from — a
  // single-company user has nothing to switch *to*. The role gate
  // above already filters out non-manager accounts.
  if (!canSwitchCompanies || accessibleCompanies.length < 2) return null

  const currentCompanyName = user?.companyName ?? t('Select company')

  return (
    <MenuTrigger>
      <Button className={styles.trigger} aria-label={t('Switch company')} data-testid="company-switcher">
        <Icon name="building" width="14" height="14" />
        <span className={styles.companyName}>{currentCompanyName}</span>
        <Icon name="swap" width="12" height="12" />
      </Button>
      <Popover isDialog={false} placement="bottom end" data-testid="company-switcher-popover">
        <Menu
          className={styles.menu}
          onAction={handleSelect}
          selectedKeys={user?.companyId ? [user.companyId] : []}
          selectionMode="single"
          data-testid="company-switcher-menu"
        >
          {accessibleCompanies.map((company) => (
            <MenuItem key={company.id} id={company.id} className={styles.menuItem}>
              {company.name}
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  )
}
